import { BuoyData } from '../types/buoy';
import { logger } from '../utils/logger';
import { BoundingBox } from '../types';

interface ActiveBuoyStation {
  id: string;
  lat: number;
  lon: number;
}

export interface NearestBuoyObservation {
  buoy: BuoyData;
  distanceKm: number;
}

export interface NearbyBuoyObservations {
  wave: NearestBuoyObservation | null;
  wind: NearestBuoyObservation | null;
}

export class NOAABuoyService {
  private static readonly BUOY_PROXY_URL = 'https://us-central1-oceaninsightpromobile.cloudfunctions.net/getBuoyObservation';
  private static readonly ACTIVE_STATIONS_URL = 'https://us-central1-oceaninsightpromobile.cloudfunctions.net/getActiveBuoyStations';
  
  // Cache for buoy data
  private static buoyCache: Map<string, { data: BuoyData; timestamp: number }> = new Map();
  private static readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  /**
   * Fetches all active buoy stations within bounds
   */
  static async fetchBuoysInBounds(bounds: BoundingBox): Promise<BuoyData[]> {
    try {
      logger.info('Fetching NOAA buoys in bounds', bounds);
      
      // Get list of active stations
      const stations = await this.fetchActiveStations(bounds);
      
      if (stations.length === 0) {
        logger.info('No buoy stations found in bounds');
        return [];
      }
      
      logger.info(`Found ${stations.length} stations in bounds`);
      
      // Fetch latest observations for each station
      const buoyPromises = stations.map(station => this.fetchBuoyData(station));
      const buoyResults = await Promise.allSettled(buoyPromises);
      
      const buoys = buoyResults.flatMap(result =>
        result.status === 'fulfilled' && result.value !== null ? [result.value] : []
      );
      
      logger.info(`Successfully fetched data for ${buoys.length} buoys`);
      return buoys;
    } catch (error) {
      logger.error('Error fetching NOAA buoys', error);
      throw error;
    }
  }

  /** Return only a recent observation close enough to represent the requested point. */
  static async fetchNearestRecentObservation(
    latitude: number,
    longitude: number,
    maxDistanceKm: number = 80,
    maxAgeMs: number = 3 * 60 * 60 * 1000,
  ): Promise<NearestBuoyObservation | null> {
    const observations = await this.fetchNearestRecentObservations(
      latitude,
      longitude,
      maxDistanceKm,
      maxAgeMs,
    );
    return observations.wave ?? observations.wind;
  }

  /** Select the nearest valid wave and wind reports independently. */
  static async fetchNearestRecentObservations(
    latitude: number,
    longitude: number,
    maxDistanceKm: number = 80,
    maxAgeMs: number = 3 * 60 * 60 * 1000,
  ): Promise<NearbyBuoyObservations> {
    const latitudeDelta = maxDistanceKm / 111;
    const longitudeScale = Math.max(Math.cos(latitude * Math.PI / 180), 0.2);
    const longitudeDelta = maxDistanceKm / (111 * longitudeScale);
    const buoys = await this.fetchBuoysInBounds({
      minLat: latitude - latitudeDelta,
      maxLat: latitude + latitudeDelta,
      minLon: longitude - longitudeDelta,
      maxLon: longitude + longitudeDelta,
    });

    const candidates = buoys
      .map(buoy => ({
        buoy,
        distanceKm: this.calculateDistanceKm(latitude, longitude, buoy.latitude, buoy.longitude),
        ageMs: Date.now() - new Date(buoy.observation_time).getTime(),
      }))
      .filter(({ buoy, distanceKm, ageMs }) =>
        distanceKm <= maxDistanceKm &&
        ageMs >= -15 * 60 * 1000 && ageMs <= maxAgeMs &&
        (buoy.wave_height !== undefined || buoy.wind_speed !== undefined)
      )
      .sort((a, b) => a.distanceKm - b.distanceKm);

    const toObservation = (candidate: typeof candidates[number] | undefined): NearestBuoyObservation | null =>
      candidate ? { buoy: candidate.buoy, distanceKm: candidate.distanceKm } : null;

    return {
      wave: toObservation(candidates.find(candidate => candidate.buoy.wave_height !== undefined)),
      wind: toObservation(candidates.find(candidate => candidate.buoy.wind_speed !== undefined)),
    };
  }

  /**
   * Fetch list of active stations from NDBC
   */
  private static async fetchActiveStations(bounds: BoundingBox): Promise<ActiveBuoyStation[]> {
    try {
      const url = `${this.ACTIVE_STATIONS_URL}?minLat=${encodeURIComponent(bounds.minLat)}` +
        `&maxLat=${encodeURIComponent(bounds.maxLat)}&minLon=${encodeURIComponent(bounds.minLon)}` +
        `&maxLon=${encodeURIComponent(bounds.maxLon)}`;
      const response = await fetch(url, {
        signal: AbortSignal.timeout(8000),
        cache: 'no-store',
      });

      if (response.ok) {
        const data: unknown = await response.json();
        if (typeof data === 'object' && data !== null &&
            'stations' in data && Array.isArray(data.stations)) {
          const stations: unknown[] = data.stations as unknown[];
          return stations
            .map((station: unknown): ActiveBuoyStation | null => {
              if (typeof station !== 'object' || station === null) {
                return null;
              }

              const { id, lat, lon } = station as { id?: unknown; lat?: unknown; lon?: unknown };
              return typeof id === 'string' &&
                typeof lat === 'number' && Number.isFinite(lat) &&
                typeof lon === 'number' && Number.isFinite(lon)
                ? { id, lat, lon }
                : null;
            })
            .filter((station): station is ActiveBuoyStation => station !== null);
        }
      }

      logger.warn('Firebase active buoy station request failed', { status: response.status });

      // Comprehensive list of North American NOAA buoy stations
      const allStations = [
        // Gulf of Mexico
        { id: '42001', lat: 25.888, lon: -89.658 }, // Mid Gulf
        { id: '42002', lat: 25.79, lon: -93.666 },  // West Gulf
        { id: '42003', lat: 25.94, lon: -85.648 },  // East Gulf
        { id: '42007', lat: 30.093, lon: -88.769 }, // Dauphin Island, AL (Nearshore)
        { id: '42012', lat: 30.065, lon: -87.555 }, // Orange Beach, AL (Nearshore)
        { id: '42035', lat: 29.232, lon: -94.413 }, // Galveston, TX
        { id: '42036', lat: 28.5, lon: -84.517 },   // West Florida
        { id: '42039', lat: 28.788, lon: -86.008 }, // Pensacola
        { id: '42040', lat: 29.208, lon: -88.207 }, // Mobile
        { id: '42041', lat: 27.945, lon: -93.563 }, // Lake Charles, LA (Nearshore)
        { id: '42055', lat: 22.12, lon: -94.046 },  // Bay of Campeche
        { id: '42056', lat: 19.82, lon: -84.945 },  // Yucatan Basin
        { id: '42067', lat: 26.065, lon: -97.217 }, // Brownsville, TX (Nearshore)
        { id: '42360', lat: 26.7, lon: -90.46 },    // South of Louisiana
        { id: 'PTAT2', lat: 27.839, lon: -97.069 }, // Port Aransas, TX (Nearshore)
        { id: 'BABT2', lat: 27.580, lon: -97.217 }, // Bob Hall Pier, TX (Nearshore)
        
        // Atlantic Coast - Southeast
        { id: '41001', lat: 34.707, lon: -72.656 }, // Cape Hatteras (Offshore)
        { id: '41002', lat: 31.903, lon: -74.836 }, // S Hatteras
        { id: '41004', lat: 32.501, lon: -79.099 }, // Charleston, SC
        { id: '41008', lat: 31.402, lon: -80.869 }, // Grays Reef, GA
        { id: '41009', lat: 28.508, lon: -80.185 }, // Cape Canaveral, FL
        { id: '41010', lat: 28.878, lon: -78.485 }, // Cape Canaveral East
        { id: '41012', lat: 30.041, lon: -80.533 }, // St. Augustine, FL
        { id: '41013', lat: 33.436, lon: -77.743 }, // Frying Pan Shoals, NC
        { id: '41025', lat: 35.001, lon: -75.383 }, // Diamond Shoals, NC (Nearshore)
        { id: '41029', lat: 32.791, lon: -79.620 }, // Offshore Charleston
        { id: '41033', lat: 32.279, lon: -80.414 }, // Fripp Island, SC (Nearshore)
        
        // Atlantic Coast - Mid-Atlantic
        { id: '44009', lat: 38.461, lon: -74.703 }, // Delaware Bay
        { id: '44014', lat: 36.611, lon: -74.836 }, // Virginia Beach
        { id: '44025', lat: 40.251, lon: -73.164 }, // Long Island
        { id: '44027', lat: 44.283, lon: -67.307 }, // Jonesport, ME
        { id: '44039', lat: 38.454, lon: -76.380 }, // Chesapeake Bay Mouth (Nearshore)
        { id: '44064', lat: 40.504, lon: -73.766 }, // New York (Nearshore)
        { id: '44065', lat: 40.369, lon: -73.703 }, // New York Harbor
        { id: '44066', lat: 39.618, lon: -72.644 }, // Long Island Sound
        { id: '44089', lat: 40.272, lon: -73.770 }, // Fire Island, NY (Nearshore)
        { id: '44091', lat: 40.970, lon: -72.072 }, // Shinnecock Inlet, NY (Nearshore)
        
        // Atlantic Coast - Northeast
        { id: '44005', lat: 43.189, lon: -69.140 }, // Gulf of Maine
        { id: '44007', lat: 43.525, lon: -70.141 }, // Portland, ME
        { id: '44008', lat: 40.504, lon: -69.247 }, // Nantucket
        { id: '44011', lat: 41.089, lon: -66.588 }, // Georges Bank
        { id: '44013', lat: 42.346, lon: -70.651 }, // Boston
        { id: '44017', lat: 40.694, lon: -72.048 }, // Montauk Point
        { id: '44018', lat: 41.255, lon: -69.305 }, // Cape Cod
        { id: '44020', lat: 41.443, lon: -70.186 }, // Nantucket Sound
        
        // Pacific Coast - California
        { id: '46011', lat: 35.0, lon: -120.992 },  // Santa Maria
        { id: '46012', lat: 37.363, lon: -122.881 }, // Half Moon Bay
        { id: '46013', lat: 38.242, lon: -123.301 }, // Bodega Bay
        { id: '46014', lat: 39.196, lon: -123.974 }, // Point Arena
        { id: '46022', lat: 40.749, lon: -124.577 }, // Eel River
        { id: '46023', lat: 34.714, lon: -120.857 }, // Point Arguello
        { id: '46025', lat: 33.749, lon: -119.053 }, // Santa Monica Basin
        { id: '46026', lat: 37.759, lon: -122.833 }, // San Francisco
        { id: '46027', lat: 41.850, lon: -124.381 }, // St. Georges
        { id: '46028', lat: 35.741, lon: -121.884 }, // Cape San Martin
        { id: '46042', lat: 36.785, lon: -122.398 }, // Monterey Bay
        { id: '46047', lat: 32.433, lon: -119.533 }, // Tanner Bank
        { id: '46053', lat: 34.252, lon: -119.841 }, // Santa Barbara East
        { id: '46054', lat: 34.265, lon: -120.477 }, // Santa Barbara West
        { id: '46059', lat: 38.047, lon: -129.969 }, // California West
        { id: '46069', lat: 33.674, lon: -120.212 }, // Point Conception
        { id: '46086', lat: 32.491, lon: -118.034 }, // San Clemente Basin
        { id: '46087', lat: 33.618, lon: -118.317 }, // San Pedro, CA (Nearshore)
        { id: '46088', lat: 40.768, lon: -124.381 }, // Cape Mendocino, CA (Nearshore)
        { id: '46221', lat: 33.855, lon: -118.633 }, // Santa Monica, CA (Nearshore)
        { id: '46222', lat: 33.619, lon: -117.946 }, // San Pedro South, CA (Nearshore)
        { id: '46224', lat: 34.734, lon: -120.628 }, // Pt Arguello, CA (Nearshore)
        { id: '46232', lat: 32.930, lon: -117.391 }, // Point Loma South, CA (Nearshore)
        { id: '46235', lat: 36.770, lon: -121.867 }, // Monterey, CA (Nearshore)
        { id: '46240', lat: 36.243, lon: -121.962 }, // Pt Sur, CA (Nearshore)
        { id: '46266', lat: 32.957, lon: -117.279 }, // Del Mar Nearshore, CA
        
        // Pacific Coast - Oregon/Washington
        { id: '46002', lat: 42.612, lon: -130.537 }, // Oregon West
        { id: '46015', lat: 42.764, lon: -124.832 }, // Port Orford
        { id: '46029', lat: 46.144, lon: -124.485 }, // Columbia River Bar
        { id: '46041', lat: 47.353, lon: -124.731 }, // Cape Elizabeth
        { id: '46050', lat: 44.641, lon: -124.526 }, // Stonewall Bank
        { id: '46089', lat: 45.925, lon: -125.819 }, // Tillamook
        { id: '46211', lat: 46.858, lon: -124.244 }, // Grays Harbor, WA (Nearshore)
        { id: '46243', lat: 46.220, lon: -124.131 }, // Willapa Bay, WA (Nearshore)
        { id: '46248', lat: 43.762, lon: -124.551 }, // Umpqua, OR (Nearshore)
        { id: '46249', lat: 42.482, lon: -124.554 }, // Cape Arago, OR (Nearshore)
        { id: '46254', lat: 42.502, lon: -124.619 }, // Rogue River, OR (Nearshore)
        { id: '46255', lat: 42.026, lon: -124.756 }, // Crescent City, CA (Nearshore)
        
        // Pacific Coast - Alaska
        { id: '46001', lat: 56.300, lon: -147.920 }, // Gulf of Alaska
        { id: '46060', lat: 60.584, lon: -146.784 }, // Seal Rocks
        { id: '46061', lat: 60.218, lon: -146.825 }, // Western Prince William Sound
        { id: '46078', lat: 55.556, lon: -152.582 }, // Albatross Bank
        { id: '46080', lat: 57.947, lon: -150.042 }, // Portlock Bank
        { id: '46082', lat: 59.688, lon: -143.406 }, // Cape Suckling
        { id: '46083', lat: 58.243, lon: -137.993 }, // Fairweather Grounds
        { id: '46084', lat: 56.600, lon: -136.100 }, // Cape Edgecumbe
        { id: '46085', lat: 55.870, lon: -142.492 }, // Central Gulf of Alaska
        
        // Great Lakes
        { id: '45001', lat: 48.017, lon: -87.777 }, // Lake Superior Mid
        { id: '45002', lat: 45.344, lon: -86.411 }, // Lake Michigan North
        { id: '45003', lat: 45.353, lon: -82.836 }, // Lake Huron North
        { id: '45004', lat: 47.582, lon: -86.589 }, // Lake Superior East
        { id: '45005', lat: 41.677, lon: -82.398 }, // Lake Erie West
        { id: '45006', lat: 47.335, lon: -89.793 }, // Lake Superior West
        { id: '45007', lat: 42.674, lon: -87.026 }, // Lake Michigan South
        { id: '45008', lat: 44.283, lon: -82.415 }, // Lake Huron South
        { id: '45012', lat: 43.525, lon: -77.845 }, // Lake Ontario
        
        // Hawaii
        { id: '51000', lat: 23.445, lon: -162.279 }, // Hawaii North
        { id: '51001', lat: 23.445, lon: -162.279 }, // Hawaii Northwest
        { id: '51002', lat: 17.094, lon: -157.807 }, // Hawaii South
        { id: '51003', lat: 19.222, lon: -160.662 }, // Hawaii West
        { id: '51004', lat: 17.525, lon: -152.382 }, // Hawaii Southeast
        { id: '51101', lat: 24.361, lon: -162.075 }, // Hawaii Northwest 200NM
        
        // Caribbean
        { id: '41046', lat: 23.822, lon: -68.384 }, // Bahamas East
        { id: '41047', lat: 27.514, lon: -71.494 }, // Bahamas Northeast
        { id: '41048', lat: 31.831, lon: -69.573 }, // Bermuda West
        { id: '41049', lat: 27.537, lon: -62.945 }, // Bermuda South
        { id: '42057', lat: 16.908, lon: -81.422 }, // Caribbean West
        { id: '42058', lat: 14.475, lon: -74.936 }, // Caribbean Central
        { id: '42059', lat: 15.252, lon: -67.483 }, // Caribbean East
        { id: '42060', lat: 16.434, lon: -63.329 }, // Caribbean Northeast
      ];
      
      // Filter stations within bounds
      const stationsInBounds = allStations
        .filter(station => 
          station.lat >= bounds.minLat &&
          station.lat <= bounds.maxLat &&
          station.lon >= bounds.minLon &&
          station.lon <= bounds.maxLon
        );
      
      return stationsInBounds;
    } catch (error) {
      logger.error('Error fetching active stations', error);
      return [];
    }
  }

  /**
   * Fetch buoy data for a specific station through the project Firebase proxy.
   * NDBC station files do not allow browser CORS and public proxy services are
   * unreliable, so web clients must not request them directly.
   */
  private static async fetchBuoyData(station: ActiveBuoyStation): Promise<BuoyData | null> {
    const { id: stationId } = station;
    try {
      // Check cache first
      const cached = this.buoyCache.get(stationId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        logger.debug(`Using cached data for station ${stationId}`);
        return cached.data;
      }

      const url = `${this.BUOY_PROXY_URL}?stationId=${encodeURIComponent(stationId)}`;

      try {
          // Fetch latest observation data
          const response = await fetch(url, {
            headers: {
              'Accept': 'text/plain',
            },
            signal: AbortSignal.timeout(7000),
          });

          if (!response.ok) {
            logger.debug(`Buoy observation unavailable for station ${stationId}`, { status: response.status });
            return null;
          }

          const text = await response.text();
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length < 3) {
            logger.warn(`Invalid data format for station ${stationId}`);
            return null;
          }
      
          // Parse header and data
          const headers = lines[0].split(/\s+/);
          const dataRows = lines.slice(2, 20).map(line => line.split(/\s+/));
          const waveHeightIndex = headers.indexOf('WVHT');
          const windSpeedIndex = headers.indexOf('WSPD');
          // NDBC rows are newest first. Keep wave and wind from one observation
          // time instead of mixing a newer wind row with an older wave row.
          const data = dataRows.find(row =>
            (waveHeightIndex >= 0 && row[waveHeightIndex] !== 'MM') ||
            (windSpeedIndex >= 0 && row[windSpeedIndex] !== 'MM')
          )
            ?? dataRows[0];

          // Get station metadata (this would come from station table in production)
          const stationMeta = this.getStationMetadata(stationId, station);

          // Parse the observation data
          const buoyData: BuoyData = {
            station_id: stationId,
            station_name: stationMeta.name,
            latitude: stationMeta.lat,
            longitude: stationMeta.lon,
            observation_time: this.parseObservationTime(data, headers),
          };
      
          // Parse wind data
          const windDirIndex = headers.indexOf('WDIR');
          const gustIndex = headers.indexOf('GST');

          if (windDirIndex >= 0 && data[windDirIndex] !== 'MM') {
            buoyData.wind_direction = parseFloat(data[windDirIndex]);
          }
          if (windSpeedIndex >= 0 && data[windSpeedIndex] !== 'MM') {
            buoyData.wind_speed = this.metersPerSecToKnots(parseFloat(data[windSpeedIndex]));
          }
          if (gustIndex >= 0 && data[gustIndex] !== 'MM') {
            buoyData.wind_gust = this.metersPerSecToKnots(parseFloat(data[gustIndex]));
          }

          // Parse wave data
          const dominantPeriodIndex = headers.indexOf('DPD');
          const avgPeriodIndex = headers.indexOf('APD');
          const waveDirIndex = headers.indexOf('MWD');

          if (waveHeightIndex >= 0 && data[waveHeightIndex] !== 'MM') {
            buoyData.wave_height = this.metersToFeet(parseFloat(data[waveHeightIndex]));
          }
          if (dominantPeriodIndex >= 0 && data[dominantPeriodIndex] !== 'MM') {
            buoyData.dominant_wave_period = parseFloat(data[dominantPeriodIndex]);
          }
          if (avgPeriodIndex >= 0 && data[avgPeriodIndex] !== 'MM') {
            buoyData.average_wave_period = parseFloat(data[avgPeriodIndex]);
          }
          if (waveDirIndex >= 0 && data[waveDirIndex] !== 'MM') {
            buoyData.wave_direction = parseFloat(data[waveDirIndex]);
          }

          // Parse atmospheric data
          const pressureIndex = headers.indexOf('PRES');
          const airTempIndex = headers.indexOf('ATMP');
          const waterTempIndex = headers.indexOf('WTMP');
          const dewPointIndex = headers.indexOf('DEWP');
          const visibilityIndex = headers.indexOf('VIS');

          if (pressureIndex >= 0 && data[pressureIndex] !== 'MM') {
            buoyData.sea_level_pressure = parseFloat(data[pressureIndex]);
          }
          if (airTempIndex >= 0 && data[airTempIndex] !== 'MM') {
            buoyData.air_temperature = this.celsiusToFahrenheit(parseFloat(data[airTempIndex]));
          }
          if (waterTempIndex >= 0 && data[waterTempIndex] !== 'MM') {
            buoyData.water_temperature = this.celsiusToFahrenheit(parseFloat(data[waterTempIndex]));
          }
          if (dewPointIndex >= 0 && data[dewPointIndex] !== 'MM') {
            buoyData.dew_point = this.celsiusToFahrenheit(parseFloat(data[dewPointIndex]));
          }
          if (visibilityIndex >= 0 && data[visibilityIndex] !== 'MM') {
            buoyData.visibility = this.metersToNauticalMiles(parseFloat(data[visibilityIndex]));
          }

          // Cache the data
          this.buoyCache.set(stationId, { data: buoyData, timestamp: Date.now() });

          return buoyData;
      } catch (error) {
        logger.debug(`Failed to fetch buoy observation for station ${stationId}`, {
          error: error instanceof Error ? error.message : String(error),
        });
        return null;
      }
    } catch (error) {
      logger.error(`Error fetching buoy data for station ${stationId}`, error);
      return null;
    }
  }

  /**
   * Get station metadata
   */
  private static getStationMetadata(
    stationId: string,
    fallback?: ActiveBuoyStation,
  ): { name: string; lat: number; lon: number } {
    const stations: Record<string, { name: string; lat: number; lon: number }> = {
      // Gulf of Mexico
      '42001': { name: 'Mid Gulf', lat: 25.888, lon: -89.658 },
      '42002': { name: 'West Gulf', lat: 25.79, lon: -93.666 },
      '42003': { name: 'East Gulf', lat: 25.94, lon: -85.648 },
      '42007': { name: 'Dauphin Island AL', lat: 30.093, lon: -88.769 },
      '42012': { name: 'Orange Beach AL', lat: 30.065, lon: -87.555 },
      '42035': { name: 'Galveston TX', lat: 29.232, lon: -94.413 },
      '42036': { name: 'West Florida', lat: 28.5, lon: -84.517 },
      '42039': { name: 'Pensacola', lat: 28.788, lon: -86.008 },
      '42040': { name: 'Mobile South', lat: 29.208, lon: -88.207 },
      '42041': { name: 'Lake Charles LA', lat: 27.945, lon: -93.563 },
      '42055': { name: 'Bay of Campeche', lat: 22.12, lon: -94.046 },
      '42056': { name: 'Yucatan Basin', lat: 19.82, lon: -84.945 },
      '42067': { name: 'Brownsville TX', lat: 26.065, lon: -97.217 },
      '42360': { name: 'South Louisiana', lat: 26.7, lon: -90.46 },
      'PTAT2': { name: 'Port Aransas TX', lat: 27.839, lon: -97.069 },
      'BABT2': { name: 'Bob Hall Pier TX', lat: 27.580, lon: -97.217 },
      
      // Atlantic Southeast
      '41001': { name: 'Cape Hatteras', lat: 34.707, lon: -72.656 },
      '41002': { name: 'S Hatteras', lat: 31.903, lon: -74.836 },
      '41004': { name: 'Charleston SC', lat: 32.501, lon: -79.099 },
      '41008': { name: 'Grays Reef GA', lat: 31.402, lon: -80.869 },
      '41009': { name: 'Cape Canaveral', lat: 28.508, lon: -80.185 },
      '41010': { name: 'Canaveral East', lat: 28.878, lon: -78.485 },
      '41012': { name: 'St Augustine FL', lat: 30.041, lon: -80.533 },
      '41013': { name: 'Frying Pan NC', lat: 33.436, lon: -77.743 },
      '41025': { name: 'Diamond Shoals NC', lat: 35.001, lon: -75.383 },
      '41029': { name: 'Off Charleston', lat: 32.791, lon: -79.620 },
      '41033': { name: 'Fripp Island SC', lat: 32.279, lon: -80.414 },
      
      // Atlantic Mid-Atlantic
      '44009': { name: 'Delaware Bay', lat: 38.461, lon: -74.703 },
      '44014': { name: 'Virginia Beach', lat: 36.611, lon: -74.836 },
      '44025': { name: 'Long Island', lat: 40.251, lon: -73.164 },
      '44027': { name: 'Jonesport ME', lat: 44.283, lon: -67.307 },
      '44039': { name: 'Chesapeake Bay', lat: 38.454, lon: -76.380 },
      '44064': { name: 'New York', lat: 40.504, lon: -73.766 },
      '44065': { name: 'New York Harbor', lat: 40.369, lon: -73.703 },
      '44066': { name: 'Long Island Sound', lat: 39.618, lon: -72.644 },
      '44089': { name: 'Fire Island NY', lat: 40.272, lon: -73.770 },
      '44091': { name: 'Shinnecock NY', lat: 40.970, lon: -72.072 },
      
      // Atlantic Northeast
      '44005': { name: 'Gulf of Maine', lat: 43.189, lon: -69.140 },
      '44007': { name: 'Portland ME', lat: 43.525, lon: -70.141 },
      '44008': { name: 'Nantucket', lat: 40.504, lon: -69.247 },
      '44011': { name: 'Georges Bank', lat: 41.089, lon: -66.588 },
      '44013': { name: 'Boston', lat: 42.346, lon: -70.651 },
      '44017': { name: 'Montauk Point', lat: 40.694, lon: -72.048 },
      '44018': { name: 'Cape Cod', lat: 41.255, lon: -69.305 },
      '44020': { name: 'Nantucket Sound', lat: 41.443, lon: -70.186 },
      
      // Pacific California
      '46011': { name: 'Santa Maria', lat: 35.0, lon: -120.992 },
      '46012': { name: 'Half Moon Bay', lat: 37.363, lon: -122.881 },
      '46013': { name: 'Bodega Bay', lat: 38.242, lon: -123.301 },
      '46014': { name: 'Point Arena', lat: 39.196, lon: -123.974 },
      '46022': { name: 'Eel River', lat: 40.749, lon: -124.577 },
      '46023': { name: 'Point Arguello', lat: 34.714, lon: -120.857 },
      '46025': { name: 'Santa Monica', lat: 33.749, lon: -119.053 },
      '46026': { name: 'San Francisco', lat: 37.759, lon: -122.833 },
      '46027': { name: 'St Georges CA', lat: 41.850, lon: -124.381 },
      '46028': { name: 'Cape San Martin', lat: 35.741, lon: -121.884 },
      '46042': { name: 'Monterey Bay', lat: 36.785, lon: -122.398 },
      '46047': { name: 'Tanner Bank', lat: 32.433, lon: -119.533 },
      '46053': { name: 'Santa Barbara E', lat: 34.252, lon: -119.841 },
      '46054': { name: 'Santa Barbara W', lat: 34.265, lon: -120.477 },
      '46059': { name: 'California West', lat: 38.047, lon: -129.969 },
      '46069': { name: 'Pt Conception', lat: 33.674, lon: -120.212 },
      '46086': { name: 'San Clemente', lat: 32.491, lon: -118.034 },
      '46087': { name: 'San Pedro CA', lat: 33.618, lon: -118.317 },
      '46088': { name: 'Cape Mendocino CA', lat: 40.768, lon: -124.381 },
      '46221': { name: 'Santa Monica CA', lat: 33.855, lon: -118.633 },
      '46222': { name: 'San Pedro South', lat: 33.619, lon: -117.946 },
      '46224': { name: 'Pt Arguello CA', lat: 34.734, lon: -120.628 },
      '46232': { name: 'Pt Loma South CA', lat: 32.930, lon: -117.391 },
      '46235': { name: 'Monterey CA', lat: 36.770, lon: -121.867 },
      '46240': { name: 'Pt Sur CA', lat: 36.243, lon: -121.962 },
      '46266': { name: 'Del Mar CA', lat: 32.957, lon: -117.279 },
      
      // Pacific Northwest
      '46002': { name: 'Oregon West', lat: 42.612, lon: -130.537 },
      '46015': { name: 'Port Orford', lat: 42.764, lon: -124.832 },
      '46029': { name: 'Columbia River', lat: 46.144, lon: -124.485 },
      '46041': { name: 'Cape Elizabeth', lat: 47.353, lon: -124.731 },
      '46050': { name: 'Stonewall Bank', lat: 44.641, lon: -124.526 },
      '46089': { name: 'Tillamook', lat: 45.925, lon: -125.819 },
      '46211': { name: 'Grays Harbor WA', lat: 46.858, lon: -124.244 },
      '46243': { name: 'Willapa Bay WA', lat: 46.220, lon: -124.131 },
      '46248': { name: 'Umpqua OR', lat: 43.762, lon: -124.551 },
      '46249': { name: 'Cape Arago OR', lat: 42.482, lon: -124.554 },
      '46254': { name: 'Rogue River OR', lat: 42.502, lon: -124.619 },
      '46255': { name: 'Crescent City CA', lat: 42.026, lon: -124.756 },
      
      // Alaska
      '46001': { name: 'Gulf of Alaska', lat: 56.300, lon: -147.920 },
      '46060': { name: 'Seal Rocks AK', lat: 60.584, lon: -146.784 },
      '46061': { name: 'Prince William', lat: 60.218, lon: -146.825 },
      '46078': { name: 'Albatross Bank', lat: 55.556, lon: -152.582 },
      '46080': { name: 'Portlock Bank', lat: 57.947, lon: -150.042 },
      '46082': { name: 'Cape Suckling', lat: 59.688, lon: -143.406 },
      '46083': { name: 'Fairweather', lat: 58.243, lon: -137.993 },
      '46084': { name: 'Cape Edgecumbe', lat: 56.600, lon: -136.100 },
      '46085': { name: 'Central Gulf AK', lat: 55.870, lon: -142.492 },
      
      // Great Lakes
      '45001': { name: 'Lake Superior Mid', lat: 48.017, lon: -87.777 },
      '45002': { name: 'Lake Michigan N', lat: 45.344, lon: -86.411 },
      '45003': { name: 'Lake Huron N', lat: 45.353, lon: -82.836 },
      '45004': { name: 'Lake Superior E', lat: 47.582, lon: -86.589 },
      '45005': { name: 'Lake Erie W', lat: 41.677, lon: -82.398 },
      '45006': { name: 'Lake Superior W', lat: 47.335, lon: -89.793 },
      '45007': { name: 'Lake Michigan S', lat: 42.674, lon: -87.026 },
      '45008': { name: 'Lake Huron S', lat: 44.283, lon: -82.415 },
      '45012': { name: 'Lake Ontario', lat: 43.525, lon: -77.845 },
      
      // Hawaii
      '51000': { name: 'Hawaii North', lat: 23.445, lon: -162.279 },
      '51001': { name: 'Hawaii NW', lat: 23.445, lon: -162.279 },
      '51002': { name: 'Hawaii South', lat: 17.094, lon: -157.807 },
      '51003': { name: 'Hawaii West', lat: 19.222, lon: -160.662 },
      '51004': { name: 'Hawaii SE', lat: 17.525, lon: -152.382 },
      '51101': { name: 'Hawaii NW 200NM', lat: 24.361, lon: -162.075 },
      
      // Caribbean
      '41046': { name: 'Bahamas East', lat: 23.822, lon: -68.384 },
      '41047': { name: 'Bahamas NE', lat: 27.514, lon: -71.494 },
      '41048': { name: 'Bermuda West', lat: 31.831, lon: -69.573 },
      '41049': { name: 'Bermuda South', lat: 27.537, lon: -62.945 },
      '42057': { name: 'Caribbean West', lat: 16.908, lon: -81.422 },
      '42058': { name: 'Caribbean Ctrl', lat: 14.475, lon: -74.936 },
      '42059': { name: 'Caribbean East', lat: 15.252, lon: -67.483 },
      '42060': { name: 'Caribbean NE', lat: 16.434, lon: -63.329 },
    };
    
    return stations[stationId] ?? {
      name: stationId,
      lat: fallback?.lat ?? 0,
      lon: fallback?.lon ?? 0,
    };
  }

  /**
   * Parse observation time from NDBC data
   */
  private static parseObservationTime(data: string[], headers: string[]): string {
    const yearIndex = headers.indexOf('#YY');
    const monthIndex = headers.indexOf('MM');
    const dayIndex = headers.indexOf('DD');
    const hourIndex = headers.indexOf('hh');
    const minuteIndex = headers.indexOf('mm');
    
    if (yearIndex >= 0 && monthIndex >= 0 && dayIndex >= 0 && hourIndex >= 0) {
      const year = data[yearIndex];
      const month = data[monthIndex].padStart(2, '0');
      const day = data[dayIndex].padStart(2, '0');
      const hour = data[hourIndex].padStart(2, '0');
      const minute = minuteIndex >= 0 ? data[minuteIndex].padStart(2, '0') : '00';
      
      return `${year}-${month}-${day}T${hour}:${minute}:00Z`;
    }
    
    return new Date().toISOString();
  }

  private static calculateDistanceKm(
    latitudeA: number,
    longitudeA: number,
    latitudeB: number,
    longitudeB: number,
  ): number {
    const toRadians = (degrees: number): number => degrees * Math.PI / 180;
    const deltaLatitude = toRadians(latitudeB - latitudeA);
    const deltaLongitude = toRadians(longitudeB - longitudeA);
    const a = Math.sin(deltaLatitude / 2) ** 2 +
      Math.cos(toRadians(latitudeA)) * Math.cos(toRadians(latitudeB)) *
      Math.sin(deltaLongitude / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Unit conversion helpers
  private static metersPerSecToKnots(mps: number): number {
    return mps * 1.94384;
  }

  private static metersToFeet(meters: number): number {
    return meters * 3.28084;
  }

  private static celsiusToFahrenheit(celsius: number): number {
    return (celsius * 9/5) + 32;
  }

  private static metersToNauticalMiles(meters: number): number {
    return meters / 1852;
  }
}
